import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Clock, AlertTriangle, CheckCircle, FileText } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import {
  PageHeader, SearchBar, Select, Table, Th, Td, TrHover, Badge,
  Modal, FormField, Input, TextArea, Button, LoadingSpinner, EmptyState,
} from '../components/UI';

const TIPO_LABELS = { FATURA_CRISE: 'Fatura Crise', ACORDO_BANCO: 'Acordo c/ Banco', ACORDO_TITULAR: 'Acordo c/ Titular' };
const TIPO_CORES  = { FATURA_CRISE: '#f97316', ACORDO_BANCO: '#8b5cf6', ACORDO_TITULAR: '#06b6d4' };
const isAcordo = (t) => t === 'ACORDO_BANCO' || t === 'ACORDO_TITULAR';

function StatCard({ label, value, subValue, color, icon: Icon }) {
  return (
    <div style={{
      background: colors.card, borderRadius: 12, padding: 16,
      border: `1px solid ${colors.border}`, flex: '1 1 180px', minWidth: 160,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: color || colors.text }}>{value}</div>
          {subValue && <div style={{ fontSize: 12, color: colors.textDim, marginTop: 2 }}>{subValue}</div>}
        </div>
        {Icon && <Icon size={20} color={color || colors.textDim} />}
      </div>
    </div>
  );
}

export default function CrisePagamentosPage({ onNavigate }) {
  const mesAtual = new Date().toISOString().slice(0, 7);
  const [faturas, setFaturas]       = useState([]);
  const [pagamentos, setPagamentos] = useState([]);
  const [stats, setStats]           = useState({});
  const [titulares, setTitulares]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filtros, setFiltros]       = useState({ search: '', mes: mesAtual, titular_id: '' });

  // Modal detalhe
  const [detalheModal, setDetalheModal] = useState(null);
  const [lancamentos, setLancamentos]   = useState([]);
  const [loadingLanc, setLoadingLanc]   = useState(false);

  // Modal pagar
  const [pagarModal, setPagarModal] = useState(null);
  const [pagarForm, setPagarForm]   = useState({
    data_pagamento: '', valor_fatura: '', valor_pago: '',
    tipo_pagamento: 'PARCIAL', forma_pagamento: '', observacoes: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (filtros.mes)        p.set('mes', filtros.mes);
    if (filtros.titular_id) p.set('titular_id', filtros.titular_id);
    const pp = new URLSearchParams();
    if (filtros.mes)        pp.set('mes_referencia', filtros.mes);
    if (filtros.titular_id) pp.set('titular_id', filtros.titular_id);
    if (filtros.search)     pp.set('search', filtros.search);
    try {
      const [fp, pag, st] = await Promise.all([
        fetch(`${API}/crise/pagamentos/faturas-pendentes?${p}`).then(r => r.json()),
        fetch(`${API}/crise/pagamentos?${pp}`).then(r => r.json()),
        fetch(`${API}/crise/pagamentos/stats`).then(r => r.json()),
      ]);
      setFaturas(Array.isArray(fp) ? fp : []);
      setPagamentos(Array.isArray(pag) ? pag : []);
      setStats(st || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filtros]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetch(`${API}/titulares`, { headers: authHeaders() }).then(r => r.json())
      .then(d => {
        const arr = Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : [];
        setTitulares(arr.filter(t => t.total_cartoes > 0));
      })
      .catch(() => {});
  }, []);

  const abrirDetalhe = async (item) => {
    setDetalheModal(item);
    const cartaoId = item.cartao_id;
    const mesRef   = item.mes_referencia;
    if (!cartaoId || !mesRef) { setLancamentos([]); return; }
    setLoadingLanc(true);
    try {
      const r = await fetch(`${API}/transacoes?cartao_id=${cartaoId}&mes=${mesRef}&limit=200`);
      const d = await r.json();
      // Blindagem: aceita só array (data ou raiz), nunca objeto de erro
      const list = Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : [];
      setLancamentos(list);
    } catch { setLancamentos([]); }
    setLoadingLanc(false);
  };

  const abrirPdf = (item, e) => {
    e?.stopPropagation();
    if (!item.cartao_id || !item.mes_referencia) return;
    // window.open não passa pelo interceptador de fetch, então o header
    // Authorization não vai — abria e voltava {"error":"Token não fornecido"}.
    const tk = localStorage.getItem('xm_token') || '';
    window.open(`${API}/faturas/pdf/${item.cartao_id}/${item.mes_referencia}?token=${encodeURIComponent(tk)}`, '_blank');
  };

  const irConciliar = (item, e) => {
    e?.stopPropagation();
    if (!item.cartao_id) return;
    localStorage.setItem('conciliacao_prefiltro', JSON.stringify({
      cartao_id: item.cartao_id,
      titular_id: item.titular_id,
      mes: item.mes_referencia,
    }));
    if (onNavigate) onNavigate('conciliacao');
  };

  const podeoPagar = (item) => !!(item.conferencia_ok || item.total_lancamentos > 0);

  const openPagar = (item, e) => {
    e?.stopPropagation();
    setPagarModal(item);
    setPagarForm({
      data_pagamento: new Date().toISOString().split('T')[0],
      valor_fatura:   String(item.fatura_atual || item.valor || ''),
      valor_pago:     '',
      tipo_pagamento: 'PARCIAL',
      forma_pagamento: '',
      observacoes:    '',
      mes_referencia:  item.mes_referencia,
    });
  };

  const salvarPagamento = async () => {
    if (!pagarModal) return;
    setSaving(true);
    try {
      await fetch(`${API}/crise/pagamentos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cartao_id:       pagarModal.cartao_id,
          mes_referencia:  pagarForm.mes_referencia || pagarModal.mes_referencia,
          data_pagamento:  pagarForm.data_pagamento,
          valor_fatura:    pagarForm.valor_fatura,
          valor_pago:      pagarForm.valor_pago,
          tipo_pagamento:  pagarForm.tipo_pagamento,
          forma_pagamento: pagarForm.forma_pagamento,
          observacoes:     pagarForm.observacoes,
        }),
      });
      setPagarModal(null);
      load();
    } catch (e) { alert('Erro: ' + e.message); }
    setSaving(false);
  };

  const hoje = new Date().toISOString().split('T')[0];
  const em7  = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

  const statusVisual = (item) => {
    if (item.status === 'ATRASADO' || (item.data_vencimento_real && item.data_vencimento_real < hoje))
      return { label: 'Atrasado', color: colors.danger };
    if (item.data_vencimento_real && item.data_vencimento_real <= em7)
      return { label: 'Vence em breve', color: colors.warning };
    return { label: 'Pendente', color: colors.textMuted };
  };

  // Filtragem local pelo search
  const faturasFiltradas = faturas.filter(f => {
    const q = (filtros.search || '').toLowerCase();
    return !q || f.nome_cartao?.toLowerCase().includes(q) || f.titular_nome?.toLowerCase().includes(q) || f.banco_nome?.toLowerCase().includes(q);
  });

  const totalPendente = faturasFiltradas.reduce((s, f) => s + (f.fatura_atual || 0), 0);
  const totalAtrasado = faturasFiltradas.filter(f => statusVisual(f).label === 'Atrasado').reduce((s, f) => s + (f.fatura_atual || 0), 0);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Pagamentos — Gestão de Crise"
        subtitle={`${faturasFiltradas.length} fatura(s) em negociação`} />

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Faturas Pendentes" icon={Clock}
          value={faturasFiltradas.length}
          subValue={fmt(totalPendente)}
          color={colors.warning} />
        <StatCard label="Atrasadas" icon={AlertTriangle}
          value={faturasFiltradas.filter(f => statusVisual(f).label === 'Atrasado').length}
          subValue={fmt(totalAtrasado)}
          color={colors.danger} />
        <StatCard label="Pago no período" icon={CheckCircle}
          value={stats.total_pagamentos || 0}
          subValue={fmt(stats.total_pago)}
          color={colors.success} />
        <StatCard label="Diferença não paga" icon={DollarSign}
          value={fmt(stats.total_diferenca)}
          color={colors.warning} />
      </div>

      {/* Filtros */}
      <SearchBar value={filtros.search} onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
        placeholder="Buscar cartão, titular, banco...">
        <Select value={filtros.titular_id} onChange={e => setFiltros(f => ({ ...f, titular_id: e.target.value }))} style={{ width: 'auto', minWidth: 160 }}>
          <option value="">Todos Titulares</option>
          {titulares.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </Select>
        <Input type="month" value={filtros.mes}
          onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))}
          style={{ width: 160 }} />
        <button onClick={() => setFiltros(f => ({ ...f, mes: '' }))}
          style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 10px', color: colors.textMuted, cursor: 'pointer', fontSize: 12 }}>
          Todos meses
        </button>
      </SearchBar>

      {/* Tabela faturas pendentes + parcelas de acordos */}
      {faturasFiltradas.length === 0 ? (
        <EmptyState message="Nenhuma fatura em negociação ou parcela de acordo pendente" icon={DollarSign} />
      ) : (
        <Table>
          <thead><tr>
            <Th>Vencimento</Th>
            <Th>Tipo</Th>
            <Th>Titular</Th>
            <Th>Cartão / Banco</Th>
            <Th>Mês</Th>
            <Th>Upload</Th>
            <Th>Fatura Total</Th>
            <Th>Conciliado</Th>
            <Th>Conferência</Th>
            <Th>Valor</Th>
            <Th>Status</Th>
            <Th align="center">Ações</Th>
          </tr></thead>
          <tbody>
            {faturasFiltradas.map(item => {
              const ehAcordo = isAcordo(item.tipo);
              const sv = statusVisual(item);
              const isAtivo = sv.label !== 'Pago';
              const dataVenc = item.data_vencimento_real || item.data_vencimento;
              const temFatura = ehAcordo
                ? (Number(item.valor) || 0) > 0
                : (item.fatura_atual || 0) > 0;
              const uploadLabel = item.upload_status === 'PROCESSADO'
                ? { text: `✓ ${item.total_transacoes || item.total_lancamentos || 0} lanç.`, color: colors.success }
                : item.upload_status === 'ENVIADO'
                  ? { text: 'Enviado', color: colors.info }
                  : { text: 'Sem upload', color: colors.textDim };
              return (
                <TrHover key={item._row_id || item.cartao_id}
                  onClick={() => !ehAcordo && abrirDetalhe(item)}
                  style={{
                    cursor: ehAcordo ? 'default' : 'pointer',
                    background: sv.label === 'Atrasado' ? `${colors.danger}08`
                      : (dataVenc && dataVenc <= em7 && isAtivo) ? `${colors.warning}08` : undefined,
                  }}>
                  <Td style={{
                    whiteSpace: 'nowrap', fontWeight: 600,
                    color: sv.label === 'Atrasado' ? colors.danger
                      : (dataVenc && dataVenc <= em7 && isAtivo) ? colors.warning : colors.text,
                  }}>
                    {dataVenc || '—'}
                  </Td>
                  {/* Tipo */}
                  <Td>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      background: `${TIPO_CORES[item.tipo] || colors.textDim}22`,
                      color: TIPO_CORES[item.tipo] || colors.textDim,
                      border: `1px solid ${TIPO_CORES[item.tipo] || colors.textDim}44`,
                      whiteSpace: 'nowrap',
                    }}>
                      {TIPO_LABELS[item.tipo] || item.tipo}
                      {ehAcordo && item.parcela_num ? ` ${item.parcela_num}/${item.parcela_total}` : ''}
                    </span>
                  </Td>
                  <Td style={{ fontSize: 12 }}>{item.titular_nome || '-'}</Td>
                  <Td style={{ fontSize: 12 }}>
                    <div style={{ fontWeight: 600 }}>
                      {item.nome_cartao || (ehAcordo ? (item.descricao || '—') : '-')}
                    </div>
                    {item.banco_nome && <div style={{ color: colors.textDim, fontSize: 11 }}>{item.banco_nome}</div>}
                  </Td>
                  <Td style={{ fontSize: 12, color: colors.textMuted }}>{item.mes_referencia}</Td>
                  {/* Upload */}
                  <Td style={{ fontSize: 11, color: uploadLabel.color }}>
                    {ehAcordo ? <span style={{ color: colors.textDim }}>—</span> : uploadLabel.text}
                  </Td>
                  {/* Fatura Total */}
                  <Td style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>
                    {ehAcordo
                      ? <span style={{ color: colors.textDim }}>—</span>
                      : (item.valor_fatura_total ? fmt(item.valor_fatura_total)
                          : <span style={{ color: colors.textDim }}>—</span>)}
                  </Td>
                  {/* Conciliado */}
                  <Td style={{ fontSize: 12, color: colors.textMuted }}>
                    {ehAcordo ? <span style={{ color: colors.textDim }}>—</span>
                      : (item.valor_conciliado != null
                          ? fmt(item.valor_conciliado)
                          : (item.total_lancamentos || item.total_transacoes) > 0
                            ? <span style={{ color: colors.textDim }}>{item.total_lancamentos || item.total_transacoes} lanç.</span>
                            : <span style={{ color: colors.textDim }}>—</span>)}
                  </Td>
                  {/* Conferência */}
                  <Td>
                    {ehAcordo ? <span style={{ color: colors.textDim, fontSize: 12 }}>—</span>
                      : ((item.total_lancamentos || item.total_transacoes) > 0
                          ? (item.conferencia_ok
                              ? <span style={{ fontSize: 11, background: `${colors.success}22`, color: colors.success, border: `1px solid ${colors.success}44`, borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>✓ OK</span>
                              : <span style={{ fontSize: 11, background: `${colors.warning}22`, color: colors.warning, border: `1px solid ${colors.warning}44`, borderRadius: 4, padding: '2px 8px', fontWeight: 600 }}>Pendente</span>)
                          : <span style={{ color: colors.textDim, fontSize: 12 }}>—</span>)}
                  </Td>
                  {/* Valor */}
                  <Td style={{ fontWeight: 700, color: temFatura ? colors.danger : colors.textDim }}>
                    {ehAcordo
                      ? (temFatura ? fmt(item.valor) : '—')
                      : (temFatura ? fmt(item.fatura_atual) : '—')}
                  </Td>
                  <Td><Badge text={sv.label} color={sv.color} /></Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}
                      onClick={e => e.stopPropagation()}>
                      {/* PDF — só se tiver upload (não aplica a acordos) */}
                      {!ehAcordo && item.upload_id && item.cartao_id && item.mes_referencia && (
                        <button onClick={(e) => abrirPdf(item, e)} title="Abrir PDF da fatura"
                          style={{ background: `${colors.primary}22`, color: colors.primary, border: `1px solid ${colors.primary}44`, borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <FileText size={12} /> PDF
                        </button>
                      )}
                      {/* Conciliar — quando tem upload mas não conciliado (não aplica a acordos) */}
                      {!ehAcordo && isAtivo && item.upload_id && !podeoPagar(item) && (
                        <button onClick={(e) => irConciliar(item, e)} title="Ir para conciliação"
                          style={{ background: `${colors.warning}22`, color: colors.warning, border: `1px solid ${colors.warning}44`, borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          Conciliar
                        </button>
                      )}
                      {/* Pagar — fatura precisa estar conciliada */}
                      {!ehAcordo && isAtivo && temFatura && (
                        <button onClick={(e) => openPagar(item, e)}
                          disabled={!podeoPagar(item)}
                          title={!podeoPagar(item) ? 'Concilie a fatura antes de pagar' : undefined}
                          style={{
                            background: podeoPagar(item) ? colors.success : colors.border,
                            color: podeoPagar(item) ? '#fff' : colors.textDim,
                            border: 'none', borderRadius: 4, padding: '4px 10px',
                            cursor: podeoPagar(item) ? 'pointer' : 'not-allowed',
                            fontSize: 11, fontWeight: 600, opacity: podeoPagar(item) ? 1 : 0.6,
                          }}>
                          Pagar
                        </button>
                      )}
                      {/* Acordo: pagamento via Contas a Pagar */}
                      {ehAcordo && isAtivo && onNavigate && (
                        <button onClick={(e) => { e.stopPropagation(); onNavigate('contas-a-pagar'); }}
                          title="Pagamentos de acordo são gerenciados em Contas a Pagar"
                          style={{ background: `${colors.info}22`, color: colors.info, border: `1px solid ${colors.info}44`, borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                          Ir p/ Contas
                        </button>
                      )}
                    </div>
                  </Td>
                </TrHover>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Histórico de pagamentos registrados */}
      {pagamentos.length > 0 && (
        <>
          <div style={{ marginTop: 32, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={14} color={colors.success} />
            <span style={{ fontSize: 13, fontWeight: 600, color: colors.textMuted }}>
              Pagamentos registrados — {pagamentos.length} registro(s)
            </span>
          </div>
          <Table>
            <thead><tr>
              <Th>Cartão</Th><Th>Titular</Th><Th>Banco</Th><Th>Mês Ref.</Th>
              <Th>Data Pgto</Th><Th>Valor Fatura</Th><Th>Valor Pago</Th><Th>Tipo</Th><Th>Forma</Th>
            </tr></thead>
            <tbody>
              {pagamentos.map(p => (
                <TrHover key={p.id}>
                  <Td style={{ fontWeight: 600, fontSize: 12 }}>{p.nome_cartao}</Td>
                  <Td style={{ fontSize: 12 }}>{p.titular_nome}</Td>
                  <Td style={{ fontSize: 12, color: colors.textMuted }}>{p.banco_nome || '-'}</Td>
                  <Td style={{ fontSize: 12 }}>{p.mes_referencia}</Td>
                  <Td style={{ fontSize: 12 }}>{p.data_pagamento}</Td>
                  <Td style={{ color: colors.danger, fontWeight: 600 }}>{fmt(p.valor_fatura)}</Td>
                  <Td style={{ color: colors.success, fontWeight: 600 }}>{fmt(p.valor_pago)}</Td>
                  <Td>
                    <Badge text={p.tipo_pagamento}
                      color={p.tipo_pagamento === 'TOTAL' ? colors.success : p.tipo_pagamento === 'PARCIAL' ? colors.warning : colors.info} />
                  </Td>
                  <Td style={{ fontSize: 12, color: colors.textMuted }}>{p.forma_pagamento || '-'}</Td>
                </TrHover>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {/* Modal: Detalhe (lançamentos) */}
      <Modal isOpen={!!detalheModal} onClose={() => { setDetalheModal(null); setLancamentos([]); }}
        title={detalheModal ? `${detalheModal.nome_cartao} — ${detalheModal.mes_referencia}` : ''}
        width={820}>
        {detalheModal && (
          <>
            <div style={{
              display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, padding: 12,
              background: colors.bg, borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 13,
            }}>
              <div><strong>Titular:</strong> {detalheModal.titular_nome}</div>
              <div><strong>Banco:</strong> {detalheModal.banco_nome || '-'}</div>
              <div><strong>Vencimento:</strong> {detalheModal.data_vencimento_real || '-'}</div>
              <div><strong>Fatura:</strong> <span style={{ color: colors.danger, fontWeight: 700 }}>{fmt(detalheModal.fatura_atual)}</span></div>
              {detalheModal.cartao_id && detalheModal.mes_referencia && (
                <button onClick={(e) => abrirPdf(detalheModal, e)}
                  style={{ marginLeft: 'auto', background: `${colors.primary}22`, color: colors.primary,
                    border: `1px solid ${colors.primary}44`, borderRadius: 6, padding: '4px 12px',
                    cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={14} /> Abrir PDF
                </button>
              )}
            </div>

            {loadingLanc ? (
              <div style={{ textAlign: 'center', padding: 32, color: colors.textDim }}>Carregando lançamentos...</div>
            ) : lancamentos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: colors.textDim }}>
                Nenhum lançamento importado para este mês.
              </div>
            ) : (
              <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                      {['Data', 'Descrição', 'Categoria', 'Valor', 'Origem', 'Parcela'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lancamentos.map((l, i) => (
                      <tr key={l.id || i} style={{ borderBottom: `1px solid ${colors.border}22`,
                        background: i % 2 === 0 ? 'transparent' : `${colors.card}44` }}>
                        <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', color: colors.textMuted }}>{l.data_compra || l.data || '—'}</td>
                        <td style={{ padding: '5px 10px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.descricao}>{l.descricao || '—'}</td>
                        <td style={{ padding: '5px 10px' }}>
                          {l.categoria ? (
                            <span style={{ background: `${colors.primary}22`, color: colors.primary, borderRadius: 4, padding: '1px 6px', fontSize: 10 }}>{l.categoria}</span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '5px 10px', whiteSpace: 'nowrap', fontWeight: 600,
                          color: (l.valor || 0) < 0 ? colors.success : colors.text }}>{fmt(l.valor)}</td>
                        <td style={{ padding: '5px 10px' }}>
                          <span style={{ fontSize: 10, color: colors.textDim, background: colors.border, borderRadius: 3, padding: '1px 5px' }}>{l.origem || '—'}</span>
                        </td>
                        <td style={{ padding: '5px 10px', color: colors.textDim }}>
                          {l.parcela_atual && l.total_parcelas ? `${l.parcela_atual}/${l.total_parcelas}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${colors.border}` }}>
                      <td colSpan={3} style={{ padding: '8px 10px', fontWeight: 700, color: colors.textMuted, fontSize: 12 }}>
                        Total ({lancamentos.length} lançamentos)
                      </td>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: colors.danger, fontSize: 13 }}>
                        {fmt(lancamentos.reduce((s, l) => s + (l.valor || 0), 0))}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
              {detalheModal && !podeoPagar(detalheModal) && (
                <Button color={colors.warning} onClick={(e) => { setDetalheModal(null); irConciliar(detalheModal, e); }}>
                  Conciliar Primeiro
                </Button>
              )}
              {detalheModal && podeoPagar(detalheModal) && (
                <Button color={colors.success} onClick={(e) => { setDetalheModal(null); openPagar(detalheModal, e); }}>
                  Registrar Pagamento
                </Button>
              )}
              <Button variant="secondary" onClick={() => { setDetalheModal(null); setLancamentos([]); }}>Fechar</Button>
            </div>
          </>
        )}
      </Modal>

      {/* Modal: Registrar Pagamento */}
      <Modal isOpen={!!pagarModal} onClose={() => setPagarModal(null)}
        title={`Registrar Pagamento — ${pagarModal?.nome_cartao || ''}`} width={500}>
        {pagarModal && (
          <>
            <div style={{
              background: colors.bg, borderRadius: 8, padding: 12, marginBottom: 16,
              border: `1px solid ${colors.border}`, fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap',
            }}>
              <div><strong>Titular:</strong> {pagarModal.titular_nome}</div>
              <div><strong>Mês:</strong> {pagarModal.mes_referencia}</div>
              <div><strong>Fatura:</strong> <span style={{ color: colors.danger, fontWeight: 700 }}>{fmt(pagarModal.fatura_atual)}</span></div>
              {pagarModal.data_vencimento_real && <div><strong>Vencimento:</strong> {pagarModal.data_vencimento_real}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <FormField label="Data do Pagamento">
                <Input type="date" value={pagarForm.data_pagamento}
                  onChange={e => setPagarForm(f => ({ ...f, data_pagamento: e.target.value }))} />
              </FormField>
              <FormField label="Tipo de Pagamento">
                <Select value={pagarForm.tipo_pagamento} onChange={e => setPagarForm(f => ({ ...f, tipo_pagamento: e.target.value }))}>
                  <option value="TOTAL">TOTAL</option>
                  <option value="PARCIAL">PARCIAL</option>
                  <option value="MINIMO">MÍNIMO</option>
                </Select>
              </FormField>
              <FormField label="Valor da Fatura (R$)">
                <Input type="number" step="0.01" value={pagarForm.valor_fatura}
                  onChange={e => setPagarForm(f => ({ ...f, valor_fatura: e.target.value }))} />
              </FormField>
              <FormField label="Valor Pago (R$)">
                <Input type="number" step="0.01" value={pagarForm.valor_pago}
                  onChange={e => setPagarForm(f => ({ ...f, valor_pago: e.target.value }))} />
              </FormField>
              <FormField label="Forma de Pagamento" style={{ gridColumn: '1 / -1' }}>
                <Select value={pagarForm.forma_pagamento} onChange={e => setPagarForm(f => ({ ...f, forma_pagamento: e.target.value }))}>
                  <option value="">Selecione...</option>
                  {['PIX', 'Boleto', 'Débito Automático', 'Transferência', 'Dinheiro', 'Outro'].map(x => <option key={x} value={x}>{x}</option>)}
                </Select>
              </FormField>
            </div>
            <FormField label="Observações (opcional)">
              <TextArea value={pagarForm.observacoes}
                onChange={e => setPagarForm(f => ({ ...f, observacoes: e.target.value }))}
                rows={2} />
            </FormField>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={() => setPagarModal(null)}>Cancelar</Button>
              <Button onClick={salvarPagamento} disabled={saving}>
                {saving ? 'Salvando...' : 'Confirmar Pagamento'}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
